import React from 'react';
import { Avatar, Button, Text, makeStyles, shorthands } from '@fluentui/react-components';
import { customColors, customFonts } from '../../../styles';

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: customColors.protivitiOffWhite,
    },
    emptyContent: {
        width: '100%',
        maxWidth: '980px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '18px',
        ...shorthands.padding('24px'),
    },
    identityRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        textAlign: 'center',
    },
    avatar: {
        width: '42px',
        height: '42px',
        ...shorthands.borderRadius('4px'),
        objectFit: 'cover',
        ...shorthands.border('1px', 'solid', '#D0D7DE'),
    },
    helperText: {
        fontFamily: customFonts.Lato,
        fontSize: '42px',
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
    },
    starters: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '10px',
        width: '100%',
    },
    starterButton: {
        flex: '0 0 calc((100% - 40px) / 5)',
        maxWidth: 'calc((100% - 40px) / 5)',
        height: '32px',
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
        backgroundColor: customColors.white,
        ...shorthands.border('1px', 'solid', '#D9D9D9'),
        borderLeftWidth: '5px',
        borderLeftColor: '#D9D9D9',
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '12px'),
        justifyContent: 'flex-start',
        transition: 'all 0.2s ease',
        '&:hover': {
            ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
            borderLeftWidth: '5px',
            backgroundColor: customColors.white,
            color: customColors.protivitiDarkBlue,
        },
        '&:active': {
            color: customColors.white,
            transition: 'all 0s',
        },
    },
    starterButtonText: {
        display: 'block',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'left',
    },
    title: {
        fontFamily: customFonts.TitleFont,
        fontSize: '28px',
        color: customColors.summarizeButtonDarker,
    },
});

interface ConversationStarterOption {
    title: string;
    text: string;
}

interface ChatWindowScreenProps {
    agentAvatarSrc?: string;
    conversationStarterOptions?: ConversationStarterOption[];
    onStarterClick?: (starter: ConversationStarterOption) => void;
}

const ChatWindowScreen: React.FC<ChatWindowScreenProps> = ({
    agentAvatarSrc,
    conversationStarterOptions,
    onStarterClick,
}) => {
    const styles = useStyles();
    const starters = conversationStarterOptions ?? [];

    return (
        <div className={styles.root}>
            <div className={styles.emptyContent}>
                <div className={styles.identityRow}>
                    {agentAvatarSrc && <Avatar className={styles.avatar} image={{ src: agentAvatarSrc }} />}
                    <Text className={styles.helperText}>Hi, how can I help you?</Text>
                </div>
                {starters.length > 0 && (
                    <div className={styles.starters}>
                        {starters.map((starter) => (
                            <Button
                                key={starter.title}
                                className={styles.starterButton}
                                appearance="transparent"
                                onClick={() => onStarterClick?.(starter)}
                            >
                                <span className={styles.starterButtonText}>{starter.title}</span>
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatWindowScreen;
